import { App as CapApp } from '@capacitor/app';
import { isNative } from './env';
import { closeInAppBrowser, bindInAppBrowserUrlChange } from './inAppBrowser';

// Listens for inbound deep links and routes them into React Router.
// Sources:
//   - Custom URL scheme: adviceev://<host>/<path>  (CFBundleURLTypes)
//   - Universal Link / App Link (when ops adds .well-known assets — Phase 4.4)
//   - Inbound https://app.advice-ev.com/* clicks
//   - In-app-browser URL changes (Yaad Sarig payment return — see below).
//
// We only need to extract the pathname and forward it to history.push.
// React Router then matches the same routes used inside the app.

const QR_NEW = /^\/[A-Za-z0-9]+\/\d+\/\d+$/;
const QR_OLD = /^\/ChargePointStation\/[^/]+\/connector\/[^/]+$/;
const APP_PATHS = [
    '/payments', '/bills', '/myGroups', '/myCars', '/RFIDCards',
    '/ChargePointStation', '/Driver', '/personal', '/settings',
    '/strategy', '/locations', '/business-group', '/favChargers',
    // spec 000211 (D2) - screens added on dev after the native layer branched off
    '/monthly-cycle', '/updateBilling',
];

function isLikelyAppPath(path) {
    if (!path) return false;
    if (QR_NEW.test(path) || QR_OLD.test(path)) return true;
    return APP_PATHS.some((p) => path === p || path.startsWith(p + '/'));
}

// Yaad Sarig payment return — detected by the signature of query params the
// gateway always appends (CCode = approval code, UID = transaction UID,
// L4digit = card last-four). This works regardless of redirect host
// (advice-ev.com in prod, dev-mobile.advice-ev.com in dev, localhost:9556
// in early dev) so no backend change is needed: whatever URL the gateway
// returns to, as long as it carries these params, we intercept it.
//
// CreditGuard return (spec 000141 / 000211): the hosted page POSTs to the BFF's
// /payment/CreditGuardReturn, which bounces to GET /updateBilling?...responseMac=...
// Success AND error/cancel both land on our own /updateBilling route, and
// UpdateBillingScreen.onLoad already decides what the params mean (the server
// verifies the result itself via inquireTransactions - matching the URL here
// grants nothing). So any navigation back to our /updateBilling closes the
// in-app browser and hands the query to the app. The bounce endpoint itself is
// deliberately NOT matched: it arrives as a POST whose params are in the body,
// and intercepting it would close the browser before the redirect carries them.
const PAYMENT_RETURN_PATHS = ['/updatebilling'];

function isPaymentReturn(parsed) {
    const q = parsed.searchParams;
    if (q.has('CCode') && q.has('UID') && q.has('L4digit')) return true; // Yaad Sarig
    const path = (parsed.pathname || '').replace(/\/$/, '').toLowerCase();
    return PAYMENT_RETURN_PATHS.includes(path);
}

async function handlePaymentReturn(history, search) {
    await closeInAppBrowser();
    history.push('/updateBilling' + (search || ''));
}

export function bindDeepLinks(history) {
    if (!isNative) return () => {};

    const appUrlSub = CapApp.addListener('appUrlOpen', ({ url }) => {
        if (!url) return;
        try {
            const parsed = new URL(url);

            // Custom scheme: adviceev://payment-return?Id=...&UserId=...&...
            // Kept for backward compatibility — backend can still send this if
            // it's ever pointed at the app's custom scheme. The new flow uses
            // the in-app-browser URL listener below.
            if (parsed.host === 'payment-return' || parsed.pathname === '/payment-return') {
                handlePaymentReturn(history, parsed.search);
                return;
            }

            const path = (parsed.pathname || '/') + (parsed.search || '');
            if (isLikelyAppPath(path)) {
                history.push(path);
            }
        } catch {
            // ignore malformed urls
        }
    });

    // In-app-browser URL interception. Yaad Sarig redirects the hosted card
    // form to https://advice-ev.com/updateBilling?Id=...&CCode=...&UID=...
    // after the user submits. This event fires *before* the WebView fully
    // loads that page — we close the overlay and push the same query into
    // React Router so UpdateBillingScreen's onLoad picks up the params and
    // calls createTokenFromTransaction.
    const urlChangePromise = bindInAppBrowserUrlChange(({ url }) => {
        // Never log the URL itself: the payment return carries cardToken, cardMask and personalId.
        if (!url) return;
        try {
            const parsed = new URL(url);
            if (isPaymentReturn(parsed)) {
                handlePaymentReturn(history, parsed.search);
            }
        } catch {
            // ignore malformed urls
        }
    });

    return () => {
        appUrlSub.then((s) => s.remove?.()).catch(() => {});
        urlChangePromise.then((unbind) => unbind()).catch(() => {});
    };
}
